import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCompass, FaCheck } from 'react-icons/fa';

const PremiumContainer = styled(motion.div)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.9) 0%,
    rgba(18, 18, 18, 0.8) 50%,
    rgba(37, 65, 178, 0.08) 100%
  );
  border-radius: var(--border-radius);
  padding: 2.5rem;
  margin-bottom: 2rem;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(37, 65, 178, 0.2);
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--secondary-color), var(--primary-color));
  }
`;

const PremiumHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
`;

const PremiumBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  color: white;
  padding: 0.5rem 1.2rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px rgba(var(--primary-rgb), 0.3);
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% { box-shadow: 0 4px 15px rgba(var(--primary-rgb), 0.3); }
    50% { box-shadow: 0 6px 20px rgba(var(--primary-rgb), 0.5); }
    100% { box-shadow: 0 4px 15px rgba(var(--primary-rgb), 0.3); }
  }
`;

const PremiumTitle = styled.h2`
  font-size: 1.8rem;
  background: linear-gradient(90deg, var(--secondary-color), var(--primary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const PremiumDescription = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto 2rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  font-size: 1rem;
  
  svg {
    color: var(--primary-color);
    font-size: 0.9rem;
    flex-shrink: 0;
  }
`;

const ContentContainer = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  h3, h4 {
    color: var(--primary-color);
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 1rem;
  }
  
  ol, ul {
    color: var(--text-secondary);
    line-height: 1.6;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  strong {
    color: var(--primary-color);
  }
`;

const PremiumContent = ({ title, description, features, children }) => {
  return (
    <PremiumContainer
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <PremiumHeader>
        <PremiumBadge>
          <FaCompass aria-hidden="true" />
          Full founder report · included
        </PremiumBadge>
        
        <PremiumTitle>{title}</PremiumTitle>
        <PremiumDescription>{description}</PremiumDescription>
        
        {features && (
          <FeaturesList>
            {features.map((feature, index) => (
              <FeatureItem key={index}>
                <FaCheck />
                {feature}
              </FeatureItem>
            ))}
          </FeaturesList>
        )}
      </PremiumHeader>
      
      <ContentContainer>
        {children}
      </ContentContainer>
    </PremiumContainer>
  );
};

export default PremiumContent;